import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { 
  Dependency, 
  DependencyType, 
  DependencyStatus, 
  DependencySeverity, 
  EntityType 
} from '../../types';
import api from '../../utils/api';

interface DependenciesState {
  dependencies: Dependency[];
  currentDependency: Dependency | null;
  isLoading: boolean;
  error: string | null;
  filters: {
    sourceType?: EntityType;
    sourceId?: string;
    targetType?: EntityType;
    targetId?: string;
    type?: DependencyType;
    status?: DependencyStatus;
    severity?: DependencySeverity;
    includeResolved: boolean;
  };
  summary: {
    total: number;
    byStatus: Record<DependencyStatus, number>;
    bySeverity: Record<DependencySeverity, number>;
    byType: Record<DependencyType, number>;
    atRisk: number;
  } | null;
}

const initialState: DependenciesState = {
  dependencies: [],
  currentDependency: null,
  isLoading: false,
  error: null,
  filters: {
    includeResolved: true,
  },
  summary: null,
};

// Async thunks
export const fetchDependencies = createAsyncThunk(
  'dependencies/fetchDependencies',
  async (params?: {
    sourceType?: EntityType;
    sourceId?: string;
    targetType?: EntityType;
    targetId?: string;
    type?: DependencyType;
    status?: DependencyStatus;
    severity?: DependencySeverity;
    includeResolved?: boolean;
  }) => {
    const queryParams = new URLSearchParams();
    if (params?.sourceType) queryParams.append('sourceType', params.sourceType);
    if (params?.sourceId) queryParams.append('sourceId', params.sourceId);
    if (params?.targetType) queryParams.append('targetType', params.targetType);
    if (params?.targetId) queryParams.append('targetId', params.targetId);
    if (params?.type) queryParams.append('type', params.type);
    if (params?.status) queryParams.append('status', params.status);
    if (params?.severity) queryParams.append('severity', params.severity);
    if (params?.includeResolved !== undefined) {
      queryParams.append('includeResolved', params.includeResolved.toString());
    }

    const response = await api.get(`/dependencies?${queryParams.toString()}`);
    return response.data;
  }
);

export const fetchDependency = createAsyncThunk(
  'dependencies/fetchDependency',
  async (id: string) => {
    const response = await api.get(`/dependencies/${id}`);
    return response.data.data;
  }
);

export const createDependency = createAsyncThunk(
  'dependencies/createDependency',
  async (dependencyData: {
    name: string;
    description?: string;
    type: DependencyType;
    sourceType: EntityType;
    sourceId: string;
    targetType: EntityType;
    targetId: string;
    severity: DependencySeverity;
    expectedDate?: string;
  }) => {
    const response = await api.post('/dependencies', dependencyData);
    return response.data.data;
  }
);

export const updateDependency = createAsyncThunk(
  'dependencies/updateDependency',
  async ({ id, ...updateData }: {
    id: string;
    name?: string;
    description?: string;
    type?: DependencyType;
    severity?: DependencySeverity;
    expectedDate?: string;
  }) => {
    const response = await api.put(`/dependencies/${id}`, updateData);
    return response.data.data;
  }
);

export const updateDependencyStatus = createAsyncThunk(
  'dependencies/updateDependencyStatus',
  async ({ id, status, resolvedDate }: {
    id: string;
    status: DependencyStatus;
    resolvedDate?: string;
  }) => {
    const response = await api.patch(`/dependencies/${id}/status`, { status, resolvedDate });
    return response.data.data;
  }
);

export const deleteDependency = createAsyncThunk(
  'dependencies/deleteDependency',
  async (id: string) => {
    await api.delete(`/dependencies/${id}`);
    return id;
  }
);

export const fetchEntityDependencies = createAsyncThunk(
  'dependencies/fetchEntityDependencies',
  async ({ entityType, entityId, direction = 'both' }: {
    entityType: EntityType;
    entityId: string;
    direction?: 'source' | 'target' | 'both';
  }) => {
    const response = await api.get(`/dependencies/entity/${entityType}/${entityId}?direction=${direction}`);
    return response.data;
  }
);

export const fetchAtRiskDependencies = createAsyncThunk(
  'dependencies/fetchAtRiskDependencies',
  async () => {
    const response = await api.get('/dependencies/at-risk');
    return response.data;
  }
);

const dependenciesSlice = createSlice({
  name: 'dependencies',
  initialState,
  reducers: {
    setFilters: (state, action: PayloadAction<Partial<DependenciesState['filters']>>) => {
      state.filters = action.payload;
    },
    clearFilters: (state) => {
      state.filters = { includeResolved: true };
    },
    clearError: (state) => {
      state.error = null;
    },
    clearCurrentDependency: (state) => {
      state.currentDependency = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch dependencies
      .addCase(fetchDependencies.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchDependencies.fulfilled, (state, action) => {
        state.isLoading = false;
        state.dependencies = action.payload.data;
        state.summary = action.payload.meta?.summary || null;
      })
      .addCase(fetchDependencies.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Failed to fetch dependencies';
      })
      
      // Fetch single dependency
      .addCase(fetchDependency.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchDependency.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentDependency = action.payload;
      })
      .addCase(fetchDependency.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Failed to fetch dependency';
      })
      
      // Create dependency
      .addCase(createDependency.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createDependency.fulfilled, (state, action) => {
        state.isLoading = false;
        state.dependencies.unshift(action.payload);
      })
      .addCase(createDependency.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Failed to create dependency';
      })
      
      // Update dependency
      .addCase(updateDependency.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateDependency.fulfilled, (state, action) => {
        state.isLoading = false;
        const index = state.dependencies.findIndex(d => d.id === action.payload.id);
        if (index !== -1) {
          state.dependencies[index] = action.payload;
        }
        if (state.currentDependency?.id === action.payload.id) {
          state.currentDependency = action.payload;
        }
      })
      .addCase(updateDependency.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Failed to update dependency';
      })
      
      // Update dependency status
      .addCase(updateDependencyStatus.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateDependencyStatus.fulfilled, (state, action) => {
        state.isLoading = false;
        const index = state.dependencies.findIndex(d => d.id === action.payload.id);
        if (index !== -1) {
          state.dependencies[index] = action.payload;
        }
        if (state.currentDependency?.id === action.payload.id) {
          state.currentDependency = action.payload;
        }
      })
      .addCase(updateDependencyStatus.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Failed to update dependency status';
      })
      
      // Delete dependency
      .addCase(deleteDependency.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteDependency.fulfilled, (state, action) => {
        state.isLoading = false;
        state.dependencies = state.dependencies.filter(d => d.id !== action.payload);
        if (state.currentDependency?.id === action.payload) {
          state.currentDependency = null;
        }
      })
      .addCase(deleteDependency.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Failed to delete dependency';
      })
      
      // Fetch entity dependencies
      .addCase(fetchEntityDependencies.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchEntityDependencies.fulfilled, (state, action) => {
        state.isLoading = false;
        state.dependencies = action.payload.data;
        state.summary = action.payload.meta?.summary || null;
      })
      .addCase(fetchEntityDependencies.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Failed to fetch entity dependencies';
      })
      
      // Fetch at-risk dependencies
      .addCase(fetchAtRiskDependencies.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchAtRiskDependencies.fulfilled, (state, action) => {
        state.isLoading = false;
        state.dependencies = action.payload.data;
        state.summary = action.payload.meta?.summary || null;
      })
      .addCase(fetchAtRiskDependencies.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Failed to fetch at-risk dependencies';
      });
  },
});

export const { setFilters, clearFilters, clearError, clearCurrentDependency } = dependenciesSlice.actions;
export default dependenciesSlice.reducer;